import {ADMIN_LANG} from "../../../settings";
import {Button, Col, Collapse, Form, Row} from "react-bootstrap";
import React, {useEffect, useState} from "react";
import {useAppDispatch, useAppSelector} from "../../../app/store";
import AdminLoader from "../AdminLoader";
import {clearError, getAdminGameSessions} from "../../../app/slices/adminGameSessions";
import toast from "react-hot-toast";
import {DataParams} from "../../../types/admin";
import {ParamKeyValuePair, useSearchParams} from "react-router-dom";
import ListPagination from "../lists/ListPagination";
import {Search} from "react-bootstrap-icons";
import {getGameSessionExcel} from "../../../app/slices/adminGameView";
import SessionTable from "../SessionTable";
import {PassedFilter, TeamFilter} from "../lists/ListFilters";
import {formatDuration} from "../utils";

type Props = {
    params: DataParams
}

const AdminGameSessions = ({params}: Props) => {
    const dispatch = useAppDispatch();
    const {results, loaded, error, count, next, previous, statistics} = useAppSelector(state => state.adminGameSession);
    const {activeTab, game_id, current_version_id} = useAppSelector(state => state.adminGameView);

    const [searchParams, setSearchParams] = useSearchParams();
    const [filtersOpen, setFiltersOpen] = useState(false);

    const sq = searchParams.get('sq') || '';
    const [searchQuery, setSearchQuery] = useState(sq);

    useEffect(() => {
        // NOTE: on React.StrictMode, this useEffect is called twice on dev mode
        if (game_id && current_version_id && activeTab === 'sessions') {
            dispatch(getAdminGameSessions({game_id, version_id: current_version_id, ...params}));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [game_id, current_version_id, activeTab, JSON.stringify(params)]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    }

    const handleFormSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        let entries: ParamKeyValuePair[] = [];
        searchParams.forEach((value, key) => {
            if (key !== 'sq') {
                entries.push([key, value]);
            }
        });
        entries.push(['sq', searchQuery]);
        setSearchParams(entries);
    }

    const clearFilters = () => {
        setSearchParams([]);
        setSearchQuery('')
    }

    const handleGetExcel = () => {
        if (game_id) {
            dispatch(getGameSessionExcel({game_id, ...params}));
        }
    }

    if (error) {
        toast.error(error, {position: 'top-right',});
        dispatch(clearError());
    }

    const filtersActive = params.team || params.passed || sq;

    return (
        <Col xs={12}>
            <Row className='gy-4 position-relative'>
                {!loaded && <AdminLoader/>}

                {statistics.played > 0 && (
                    <Col xs={12}>
                        <div className='admin-card'>
                            <div className='d-flex flex-wrap justify-content-around'>
                                <div className='text-center me-4'>
                                    <strong>{ADMIN_LANG.games_played}:</strong>
                                    <div>{statistics.played}</div>
                                </div>
                                <div className='text-center me-4'>
                                    <strong>Uniikit pelaajat:</strong>
                                    <div>{statistics.unique_users}</div>
                                </div>
                                {statistics.average_points !== null && (
                                    <div className='text-center me-4'>
                                        <strong>{ADMIN_LANG.average_points}:</strong>
                                        <div>{statistics.average_points}</div>
                                    </div>
                                )}
                                {statistics.max_points !== null && (
                                    <div className='text-center me-4'>
                                        <strong>Korkeimmat pisteet:</strong>
                                        <div>{statistics.max_points}</div>
                                    </div>
                                )}
                                {statistics.average_duration !== null && (
                                    <div className='text-center'>
                                        <strong>{ADMIN_LANG.average_duration}:</strong>
                                        <div>{formatDuration(statistics.average_duration.toString())}</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Col>
                )}
                <Col xs={12}>
                    <div className='admin-card'>
                        <Row className='gy-3'>
                            <Col xs={12}>
                                <Button
                                    onClick={() => setFiltersOpen(!filtersOpen)}
                                    aria-controls='extra-filters'
                                    aria-expanded={filtersOpen}
                                    variant='primary btn-sm'>
                                    {ADMIN_LANG.filter}
                                </Button>
                                {filtersActive && <Button className='ms-2' variant='outline-primary btn-sm'
                                                          onClick={clearFilters}>{ADMIN_LANG.clear_filters}</Button>}
                                <Button variant='info btn-sm' className='ms-2'
                                        onClick={handleGetExcel}>{ADMIN_LANG.export}</Button>
                            </Col>
                            <Form onSubmit={handleFormSubmit} method='get' className='list-filters col-12'>
                                <Row className='pb-3 gy-2'>
                                    <Col xs={8} md={6} xl={4} className='search-input'>
                                        <Form.Control type='text' value={searchQuery} onChange={handleSearchChange}
                                                      placeholder={ADMIN_LANG.search}/>
                                        <Button type='submit' className='search-button'><Search/></Button>
                                    </Col>
                                    <Col xs={4} md={6} xl={8} className='d-flex justify-content-end align-items-center'>
                                    </Col>
                                    <Col xs={12}>
                                        <Collapse in={filtersOpen}>
                                            <Row>
                                                <TeamFilter/>
                                                <PassedFilter/>
                                            </Row>
                                        </Collapse>
                                    </Col>
                                </Row>
                            </Form>
                        </Row>
                        <SessionTable sessions={results}/>
                    </div>
                </Col>
                <ListPagination count={count} next={next} previous={previous}/>
            </Row>
        </Col>
    );
}

export default AdminGameSessions;